import React from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { TemplateVariable } from '../../../../../../types/communication';
import VariablesPanel from '../../shared/VariablesPanel';
import { unusedVariableNames, usedConstantNames } from '../studioDraft';
import { SYSTEM_CONSTANTS } from '../../../../../../utils/communicationConstants';
import { MessageCompositionStudio } from '../useMessageCompositionStudio';

const VariablesSection: React.FC<{ studio: MessageCompositionStudio }> = ({ studio }) => {
    const { meta, templates, readOnly } = studio;
    if (!meta) return null;

    const variables: TemplateVariable[] = [];
    const seen = new Set<string>();
    for (const template of templates) {
        for (const variable of template.variables) {
            if (seen.has(variable.name)) continue;
            seen.add(variable.name);
            variables.push(variable);
        }
    }

    const unusedEverywhere = (name: string): boolean =>
        templates
            .filter((t) => t.variables.some((v) => v.name === name))
            .every((t) => unusedVariableNames(t, meta.channelConfig).includes(name));
    const unused = variables.map((v) => v.name).filter(unusedEverywhere);

    const constantNames = new Set<string>();
    for (const template of templates) {
        for (const name of usedConstantNames(template, meta.channelConfig)) constantNames.add(name);
    }
    const constants = SYSTEM_CONSTANTS.filter((c) => constantNames.has(c.name));

    const templatesWith = (name: string) => templates.filter((t) => t.variables.some((v) => v.name === name));

    const update = (name: string, patch: Partial<TemplateVariable>) =>
        templatesWith(name).forEach((t) =>
            studio.updateTemplate(t.id, {
                variables: t.variables.map((v) => (v.name === name ? { ...v, ...patch } : v)),
            }),
        );

    const remove = (name: string) =>
        templatesWith(name).forEach((t) =>
            studio.updateTemplate(t.id, { variables: t.variables.filter((v) => v.name !== name) }),
        );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-start gap-2 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-900 px-4 py-3 text-xs text-sky-900 dark:text-sky-200">
                <HiOutlineInformationCircle size={16} className="mt-0.5 shrink-0" />
                <span>
                    As variáveis são detectadas sozinhas no conteúdo: escreva <code className="font-mono">{'{{NOME}}'}</code> no texto,
                    no assunto, nos destinatários ou no anexo e ela aparece aqui. Dê um rótulo claro — é ele que aparece no formulário de
                    quem envia a mensagem.
                </span>
            </div>

            <VariablesPanel
                variables={variables}
                unused={unused}
                constants={constants}
                disabled={readOnly}
                onUpdate={update}
                onRemove={remove}
            />
        </div>
    );
};

export default VariablesSection;
